import {
    Contact,
    EventCore,
    ExternalLink,
    ExternalSourceType,
    Feature,
    Location,
    Product,
    ScheduleItem,
    SeasonEvent,
    StripeAccount,
    UserAccount,
} from '@reg/shared/model';
import { Division } from '@reg/shared/model/division.model';
import { Mat } from '@reg/shared/model/mat.model';
import { addDays, differenceInDays, format, isAfter, isWithinInterval, subDays } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import get from 'lodash-es/get';

import { Gender } from '@reg/shared/model/gender.model';
import { DateFormat } from '../enum';
import { convertToDateTime } from '../helper/dateTime-helper';
import { BaseEvent } from './base-event.model';
import { ReferenceItem } from './reference-item.model';

export interface IStartEndTime {
    dayIndex: number;
    startDateTime: Date;
    endDateTime: Date;
}

interface IAttributeJson {
    startEndTimes: IStartEndTime[],
}

/**
 * Model used to hold Event data
 */
export class Event extends BaseEvent {
    public static APPROVAL_STATUS_PENDING: string = 'pending';

    public isNew: boolean;
    public isParticipantWaiverRequired: boolean;
    public location: Location;
    public siteId: number;
    public features: Feature[];
    public divisions: Division[];
    public products: Product[];
    public scheduleItems: ScheduleItem[];
    public floLevels: ReferenceItem[] = [];
    public externalLinks: ExternalLink[] = [];
    public seasons: SeasonEvent[] = [];
    public contacts: Contact[];
    public mats: Mat[];
    public genders: Gender[];
    public isVisible: boolean;
    public createdByUserAccount: UserAccount;
    public stripeAccount: StripeAccount = new StripeAccount();
    public maxWrestlerCount: number = null;
    public participantAlias: string;
    public participantAliasPlural: string;
    public description: string;
    public websiteUrl: string;
    public isDual: boolean;
    public isSetupComplete: boolean = false;
    public isPresetTeams: boolean = false;
    public usawEventId: string;
    public resultEmailsSentDateTime: Date;
    public registrationReceiptMsg: string;
    public original: Event;
    public linkedCoreEventId: string;
    public attributeJson: IAttributeJson;

    public static getFields(): string[] {
        return [
            'name',
            'timeZone',
            'startDateTime',
            'endDateTime',
            'isParticipantWaiverRequired',
            'location',
            'approvalStatus',
            'siteId',
            'features',
            'divisions',
            'products',
            'scheduleItems',
            'externalLinks',
            'contacts',
            'isVisible',
            'createdByUserId',
            'createdByUserAccount',
            'stripeAccountId',
            StripeAccount.NAME,
            'maxWrestlerCount',
            'participantAlias',
            'participantAliasPlural',
            'description',
            'websiteUrl',
            'isDual',
            'isSetupComplete',
            'isPresetTeams',
            'mats',
            'resultEmailsSentDateTime',
            'seasons',
            'registrationReceiptMsg',
            'attributeJson',
            'genders',
        ];
    }

    public clone(): Event {
        const clone: Event = new Event(this.siteId);
        clone.id = this.id;
        clone.isNew = this.isNew;
        clone.name = this.name;
        clone.timeZone = this.timeZone;
        clone.startDateTime = this.startDateTime && new Date(this.startDateTime.getTime());
        clone.endDateTime = this.endDateTime && new Date(this.endDateTime.getTime());
        clone.isParticipantWaiverRequired = this.isParticipantWaiverRequired;
        clone.location = this.location && this.location.clone();
        clone.approvalStatus = this.approvalStatus;
        clone.siteId = this.siteId;
        clone.features = this.features && this.features.map((f: Feature) => f.clone());
        clone.products = this.products && this.products.map((p: Product) => p.clone());
        clone.scheduleItems = this.scheduleItems && this.scheduleItems.map((s: ScheduleItem) => s.clone());
        clone.floLevels = this.floLevels && this.floLevels.map((ri: ReferenceItem) => ri.clone());
        clone.genders = this.genders && [...this.genders];
        clone.externalLinks = this.externalLinks && this.externalLinks.map((ex: ExternalLink) => ex.clone());
        clone.seasons = this.seasons && this.seasons.map((oe: SeasonEvent) => oe.clone());
        clone.contacts = this.contacts && this.contacts.map((c: Contact) => c.clone());
        clone.divisions = this.divisions && this.divisions.map((d: Division) => d.clone());
        clone.mats = this.mats && this.mats.map((m: Mat) => m.clone());
        clone.isVisible = this.isVisible;
        clone.createdByUserAccount = this.createdByUserAccount && this.createdByUserAccount.clone();
        clone.stripeAccount = this.stripeAccount && this.stripeAccount.clone();
        clone.maxWrestlerCount = this.maxWrestlerCount;
        clone.participantAlias = this.participantAlias;
        clone.participantAliasPlural = this.participantAliasPlural;
        clone.description = this.description;
        clone.websiteUrl = this.websiteUrl;
        clone.isDual = this.isDual;
        clone.isSetupComplete = this.isSetupComplete;
        clone.isPresetTeams = this.isPresetTeams;
        clone.original = this.original && this.original.clone();
        clone.resultEmailsSentDateTime =
            this.resultEmailsSentDateTime && new Date(this.resultEmailsSentDateTime.getTime());
        clone.registrationReceiptMsg = this.registrationReceiptMsg;
        clone.linkedCoreEventId = this.linkedCoreEventId;
        clone.attributeJson = cloneDeep(this.attributeJson);
        return clone;
    }

    public hasEnded(daysAgo: number = null): boolean {
        if (!daysAgo) {
            const nowLocal: Date = new Date();

            if (isAfter(nowLocal, this.endDateTime)) {
                return true;
            }
        } else {
            return differenceInDays(new Date(), this.endDateTime) >= daysAgo;
        }
    }

    public getFormattedDate(): string {
        if (this.startDateTime && this.endDateTime) {
            if (this.startDateTime.toDateString() === this.endDateTime.toDateString()) {
                return format(this.startDateTime, DateFormat.MMDDYYYY);
            }
            return `${format(this.startDateTime, DateFormat.MMDDYYYY)} - ${
                format(this.endDateTime, DateFormat.MMDDYYYY)}`;
        }

        return null;
    }

    public getFormattedDateTime(): string {
        if (this.startDateTime && this.endDateTime) {
            if (this.startDateTime.toDateString() === this.endDateTime.toDateString()) {
                return `${format(this.startDateTime, DateFormat.MDYhma)} - ${
                    format(this.endDateTime, DateFormat.hma)}`;
            }
            return `${format(this.startDateTime, DateFormat.MDYhma)} - ${
                format(this.endDateTime, DateFormat.MDYhma)}`;
        }

        return null;
    }

    public getFormattedLocation(): string {
        if (!this.location) {
            return '';
        }

        const parts: string[] = [];
        parts.push(this.location.city || this.location.name);

        if (this.location.state) {
            parts.push(this.location.state);
        }

        return parts.join(', ');
    }

    public getFormattedAddress(): string {
        if (!this.location) {
            return '';
        }

        const parts: string[] = [];
        parts.push(this.location.city);

        if (this.location.state) {
            parts.push(this.location.state);
        }

        if (this.location.country) {
            parts.push(this.location.country);
        }

        return parts.join(', ');
    }

    constructor(siteId?: number, id?: string) {
        super();
        this.id = id;
        this.approvalStatus = Event.APPROVAL_STATUS_PENDING;
        this.isVisible = false;
        this.siteId = siteId;
        this.timeZone = 'America/Chicago';
        this.isNew = true;
        this.location = new Location();
        this.attributeJson = {
            startEndTimes: [],
        };
    }

    /**
     * Add the new feature if it doesn't already exist
     * @param {Feature} feature
     */
    public addFeature(feature: Feature): void {
        if (!feature ||
            !feature.id ||
            this.hasFeature(feature.id)) {
            return;
        }

        this.features = this.features || [];
        this.features.push(feature);
    }

    /**
     * Determines if this event is using the specified feature
     */
    public hasFeature(featureId: string): boolean {
        if (this.features) {
            return this.features.filter((e: Feature) => e.id === featureId).length === 1;
        }

        return false;
    }

    /**
     * Get the first external link found that matches the given source
     */
    public getExternalLink(externalSource: ExternalSourceType): ExternalLink {
        return (this.externalLinks || []).find((ex: ExternalLink) => ex.externalSource === externalSource);
    }

    /**
     * Get the trackwrestling external link
     */
    public getTrackwrestlingExternalLink(): ExternalLink {
        return this.getExternalLink(ExternalSourceType.TRACKWRESTLING);
    }

    /**
     * Get the eventbrite external link
     */
    public getEventbriteExternalLink(): ExternalLink {
        return this.getExternalLink(ExternalSourceType.EVENTBRITE);
    }

    /**
     * Determines if this event is using registration
     */
    public hasRegistration(): boolean {
        return this.hasFeature(Feature.REGISTRATION_ID);
    }

    /**
     * Determines if this event has event management linked to it
     */
    public hasEventMgmt(): boolean {
        return this.hasFeature(Feature.EVENT_MGMNT_ID);
    }

    /**
     * Determines if this event has streaming
     */
    public hasStreaming(): boolean {
        return this.hasFeature(Feature.STREAMING_ID);
    }

    /**
     * Determines if the core event is within acceptable range
     */
    public isCoreEventWithinRange(coreEvent: EventCore): boolean {
        if (!coreEvent) {
            return false;
        }

        return isWithinInterval(
            coreEvent.startDateTime,
            { start: subDays(this.startDateTime, 1), end: addDays(this.startDateTime, 1) },
        ) && isWithinInterval(
            coreEvent.endDateTime,
            { start: subDays(this.endDateTime, 1), end: addDays(this.endDateTime, 1) },
        );
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'event';
        json.data.id = this.id;
        json.data.attributes = {};

        // Only update isVisible, approvalStatus, and isSetupComplete if they changed
        if (this.original) {
            if (this.original.isVisible !== this.isVisible) {
                json.data.attributes.isVisible = this.isVisible;
            }
            if (this.original.approvalStatus !== this.approvalStatus) {
                json.data.attributes.approvalStatus = this.approvalStatus;
            }
            if (this.original.isSetupComplete !== this.isSetupComplete) {
                json.data.attributes.isSetupComplete = this.isSetupComplete;
            }
        } else {
            json.data.attributes.isVisible = this.isVisible;
            json.data.attributes.approvalStatus = this.approvalStatus;
            json.data.attributes.isSetupComplete = this.isSetupComplete;
        }

        json.data.attributes.name = this.name;
        json.data.attributes.siteId = this.siteId;
        json.data.attributes.startDateTimeUtc = this.startDateTime.toISOString();
        json.data.attributes.endDateTimeUtc = this.endDateTime.toISOString();
        json.data.attributes.maxWrestlerCount =
            this.maxWrestlerCount > 0 ? parseInt(this.maxWrestlerCount.toString(), null) : null;
        json.data.attributes.description = this.description || null;
        json.data.attributes.websiteUrl = this.websiteUrl || null;
        json.data.attributes.isDual = this.isDual || false;
        json.data.attributes.isPresetTeams = this.isPresetTeams || false;
        json.data.attributes.timeZone = this.timeZone;
        json.data.attributes.registrationReceiptMsg = this.registrationReceiptMsg || null;
        json.data.attributes.attributeJson = this.attributeJson || null;
        json.data.attributes.genders = this.genders || null;

        if (this.createdByUserAccount && this.createdByUserAccount.id) {
            json.data.attributes.createdByUserId = this.createdByUserAccount.id.toString();
        }

        if (this.location !== null) {
            json.data.attributes.location = this.location.serialize();
        }

        if (this.attributeJson.startEndTimes.length) {
            json.data.attributes.attributeJson.startEndTimes = this.attributeJson.startEndTimes.map((date) => ({
                dayIndex: date.dayIndex,
                startDateTime: date.startDateTime.toISOString(),
                endDateTime: date.endDateTime.toISOString(),
            }));
        }

        json.data.relationships = {};

        if (this.features) {
            const features: any = {};
            features.data = [];

            for (const feature of this.features) {
                features.data.push({ type: 'feature', id: feature.id });
            }

            json.data.relationships.features = features;
        }

        // Every event should have a floLevel so don't allow the possibility of them being cleared.
        if (this.floLevels?.length) {
            const floLevels: any = {};
            floLevels.data = [];

            for (const floLevel of this.floLevels) {
                floLevels.data.push({ type: 'floLevel', id: floLevel.id });
            }

            json.data.relationships.floLevels = floLevels;
        }

        // Save the StripeAccount relationship
        if (this.stripeAccount && this.stripeAccount.id) {
            json.data.relationships.stripeAccount = { data: { type: 'stripeAccount', id: this.stripeAccount.id } };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize to a json api format
     */
    public deserialize(json: any, includedData: any[] = null): Event {
        const data: any = json.data || json;
        const included: any[] = includedData || json.included || [];
        const attrs: any = data.attributes || {};
        const relationships: any = data.relationships || {};

        this.id = data.id;
        this.isNew = false;
        this.name = attrs.name;
        this.isParticipantWaiverRequired = attrs.isParticipantWaiverRequired;
        this.approvalStatus = attrs.approvalStatus;
        this.isVisible = attrs.isVisible;
        this.startDateTime = convertToDateTime(attrs.startDateTime);
        this.endDateTime = convertToDateTime(attrs.endDateTime);
        this.location = (new Location()).deserialize(attrs.location);
        this.maxWrestlerCount = attrs.maxWrestlerCount;
        this.participantAlias = attrs.participantAlias;
        this.participantAliasPlural = attrs.participantAliasPlural;
        this.description = attrs.description;
        this.websiteUrl = attrs.websiteUrl;
        this.isDual = attrs.isDual;
        this.isSetupComplete = attrs.isSetupComplete;
        this.isPresetTeams = attrs.isPresetTeams;
        this.resultEmailsSentDateTime = (attrs.resultEmailsSentDateTime &&
            convertToDateTime(attrs.resultEmailsSentDateTime)) || null;
        this.registrationReceiptMsg = attrs.registrationReceiptMsg;
        this.genders = attrs.genders;

        if (attrs.siteId) {
            this.siteId = parseInt(attrs.siteId, null);
        }

        this.timeZone = attrs.timeZone;
        this.location.timeZone = this.timeZone;

        if (attrs.attributeJson?.startEndTimes?.length) {
            this.attributeJson.startEndTimes = attrs.attributeJson.startEndTimes.map((day) => ({
                dayIndex: day.dayIndex,
                startDateTime: convertToDateTime(day.startDateTime),
                endDateTime: convertToDateTime(day.endDateTime),
            }));
        }

        const featureLinks: any = relationships.features;
        if (featureLinks &&
            featureLinks.data) {
            this.features = [];

            for (const featureLink of featureLinks.data) {
                const feature: any = included.filter((i: any) => i.id === featureLink.id)[0];
                if (feature) {
                    this.features.push((new Feature()).deserialize(feature));
                }
            }
        }

        const floLevels: any = relationships.floLevels;
        if (floLevels &&
            floLevels.data) {
            this.floLevels = [];

            for (const referenceItem of floLevels.data) {
                const ref: any = included.find((i: any) => i.id === referenceItem.id);
                if (ref) {
                    this.floLevels.push((new ReferenceItem()).deserialize(ref));
                }
            }
        }

        const scheduleItemLinks: any = relationships.scheduleItems;
        if (scheduleItemLinks &&
            scheduleItemLinks.data) {
            this.scheduleItems = [];

            for (const scheduleItemLink of scheduleItemLinks.data) {
                const scheduleItem: any = included.find((i: any) => i.id === scheduleItemLink.id);
                if (scheduleItem) {
                    this.scheduleItems.push((new ScheduleItem(this.id)).deserialize(scheduleItem));
                }
            }
        }

        const externalLinks: any[] = get(data, 'relationships.externalLinks.data') || [];
        externalLinks.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.externalLinks.push((new ExternalLink()).deserialize(item));
            }
        });

        const contactLinks: any = relationships.contacts;
        if (contactLinks &&
            contactLinks.data) {
            this.contacts = [];

            for (const contactLink of contactLinks.data) {
                const contact: any = included.find((i: any) => i.id === contactLink.id);
                if (contact) {
                    this.contacts.push((new Contact()).deserialize(contact));
                }
            }
        }

        const productLinks: any = relationships.products;
        if (productLinks &&
            productLinks.data) {
            this.products = [];

            for (const productLink of productLinks.data) {
                const productInclude: any = included.filter((i: any) => i.id === productLink.id)[0];

                if (productInclude) {
                    const product: Product = (new Product()).deserialize(productInclude, included);

                    if (product.isActive) {
                        this.products.push(product);
                    }
                }
            }
        }

        const divisionLinks: any = relationships.divisions;
        if (divisionLinks &&
            included &&
            divisionLinks.data) {
            this.divisions = [];

            for (const divisionLink of divisionLinks.data) {
                const divisionInclude: any = included.find((i: any) => i.id === divisionLink.id);

                if (divisionInclude) {
                    const division: Division = (new Division(this.id)).deserialize(divisionInclude, included);
                    this.divisions.push(division);
                }
            }
        }

        const matLinks: any = relationships.mats;
        if (matLinks &&
            included &&
            matLinks.data) {
            this.mats = [];

            for (const matLink of matLinks.data) {
                const matInclude: any = included.find((i: any) => i.id === matLink.id);

                if (matInclude) {
                    const mat: Mat = (new Mat()).deserialize(matInclude);
                    this.mats.push(mat);
                }
            }
        }

        // Fill createdByUserAccount from relationships if it exists
        const createdByUserId: string = get(data, 'relationships.createdByUserAccount.data.id');
        const createdByUser: any = included.find((i: any) => i.type === 'userAccount' && i.id === createdByUserId);

        if (createdByUser) {
            this.createdByUserAccount = (new UserAccount()).deserialize(createdByUser);
        } else if (attrs.createdByUserId) {
            this.createdByUserAccount = new UserAccount();
            this.createdByUserAccount.id = attrs.createdByUserId;
        }

        // Fill stripeAccount from relationships if it exists
        const stripeAccountId: string = get(data, 'relationships.stripeAccount.data.id');
        const stripeAccount: any = included.find((i: any) => i.type === StripeAccount.NAME && i.id === stripeAccountId);

        if (stripeAccount) {
            this.stripeAccount = (new StripeAccount()).deserialize(stripeAccount);
        } else {
            this.stripeAccount = new StripeAccount(attrs.stripeAccountId);
        }

        const orgEvents: any[] = get(data, 'relationships.seasons.data') || [];
        orgEvents.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.seasons.push((new SeasonEvent()).deserialize(item));
            }
        });

        this.original = this.clone();

        return this;
    }
}
